import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { Text } from '@/components/common/text';
import { BottomSheetModal } from '@/components/residents/marketplace/shared/BottomSheetModal';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

export interface MonthOption {
  label: string;
  value: string;
}

interface MonthPickerBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  options: MonthOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
}

/**
 * Month Picker Bottom Sheet
 * Matches Figma designs 3.d and 3.e - Dropdown list for First/Last payment
 * Shows a list of month options with the selected item highlighted
 */
export const MonthPickerBottomSheet: React.FC<MonthPickerBottomSheetProps> = ({
  visible,
  onClose,
  title,
  options,
  selectedValue,
  onSelect,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const { colors } = useThemeColors();

  const handleSelect = (value: string) => {
    onSelect(value);
    onClose();
  };

  return (
    <BottomSheetModal
      visible={visible}
      onClose={onClose}
      title={title}
      scrollable={false}
    >
      <ScrollView style={styles.listContainer} showsVerticalScrollIndicator={false}>
        {options.map((option) => {
          const isSelected = option.value === selectedValue;
          return (
            <TouchableOpacity
              key={option.value}
              style={styles.listItem}
              onPress={() => handleSelect(option.value)}
              activeOpacity={0.7}
            >
              {isSelected && (
                <View style={styles.selectedIndicator}>
                  <View style={[styles.selectedDot, { backgroundColor: MARKETPLACE_COLORS.textPrimary }]} />
                </View>
              )}
              <Text style={[
                styles.listItemText,
                { color: colors.text.muted },
                isSelected && [styles.listItemTextSelected, { color: MARKETPLACE_COLORS.textPrimary }],
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </BottomSheetModal>
  );
};

const styles = StyleSheet.create({
  listContainer: {
    maxHeight: 280,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
    paddingVertical: 9.5, // 40 − 21 line, split
    gap: 12,
  },
  selectedIndicator: {
    width: 16,
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 2,
  },
  selectedDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
  },
  listItemText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
  listItemTextSelected: {
    fontWeight: '500',
  },
});

export default MonthPickerBottomSheet;
